// Search using Nominatim (OpenStreetMap's free geocoder)
import { useState, useEffect, useRef } from 'react';

interface SearchBoxProps {
  onSelect: (location: { lat: number; lng: number; name: string }) => void;
  placeholder?: string;
}

interface NominatimResult {
  place_id: number;
  display_name: string;
  name?: string;
  lat: string;
  lon: string;
  type?: string;
}

export function SearchBox({ onSelect, placeholder = 'Tìm địa chỉ...' }: SearchBoxProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleInputChange(value: string) {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < 3) {
      setResults([]);
      return;
    }
    setLoading(true);
    debounceRef.current = window.setTimeout(async () => {
      try {
        // Nominatim API — free, requires User-Agent (browser sends automatically)
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&countrycodes=vn&limit=5&addressdetails=0`,
          { headers: { 'Accept-Language': 'vi,en' } }
        );
        if (!res.ok) throw new Error('Search failed');
        const data = (await res.json()) as NominatimResult[];
        setResults(data);
        setShowDropdown(true);
      } catch (err) {
        console.error('Search error:', err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 400);
  }

  function handleSelect(result: NominatimResult) {
    onSelect({
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon),
      name: result.display_name,
    });
    setQuery(result.display_name);
    setShowDropdown(false);
  }

  // Parse display_name into main + secondary
  function splitDisplayName(name: string): { main: string; secondary: string } {
    const parts = name.split(',');
    if (parts.length <= 1) return { main: name, secondary: '' };
    return { main: parts[0].trim(), secondary: parts.slice(1).join(',').trim() };
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white shadow-sm"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-600"></div>
          </div>
        )}
        {!loading && query && (
          <button
            onClick={() => { setQuery(''); setResults([]); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        )}
      </div>

      {showDropdown && results.length > 0 && (
        <ul className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-y-auto z-30">
          {results.map((r) => {
            const { main, secondary } = splitDisplayName(r.display_name);
            return (
              <li
                key={r.place_id}
                onClick={() => handleSelect(r)}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
              >
                <div className="font-medium text-gray-900">{main}</div>
                {secondary && <div className="text-xs text-gray-500">{secondary}</div>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
